/**
 * Stories — the editorially-curated travel articles on the trips home of both clients, managed in the admin
 * and stored at `stories/{id}`.
 *
 * Two levels of strictness live here on purpose:
 *
 * - The STORED shape ({@link StoryBlockSchema}, `StoryModelSpec`) is structural only. A draft is saved as the
 *   editor leaves it — an empty text block, a data block with no entities yet, no cover — so nothing here
 *   demands completeness, and a section icon is any string (an icon added after a build shipped must not
 *   make that build drop the section).
 * - Completeness is checked where a story goes live: the server's publish checks, and the clients'
 *   {@link PublishedStorySchema}, which drops a story that is somehow incomplete instead of rendering half
 *   of it.
 *
 * Content is read block by block ({@link parseStoryBlocks}): an older app build meeting a block kind added
 * later skips that block and renders the rest of the article.
 */
import { z } from 'zod';

/** The specialists who can author a story. Their names, roles and avatars stay in the clients' code. */
export const STORY_AUTHOR_IDS = ['natalie', 'shannon'] as const;
export const StoryAuthorIdSchema = z.enum(STORY_AUTHOR_IDS);
export type StoryAuthorId = z.infer<typeof StoryAuthorIdSchema>;

/** The entity collections a story can reference. */
export const STORY_ITEM_TYPES = ['hotel', 'destination', 'place'] as const;
export const StoryItemTypeSchema = z.enum(STORY_ITEM_TYPES);
export type StoryItemType = z.infer<typeof StoryItemTypeSchema>;

/**
 * The icons an editor can give a section header. Every one has an SF Symbol mapping on mobile (the native
 * section menu shows nothing for an unmapped name), which `mobile/src/ui/__tests__/sf-symbols.test.ts`
 * pins. Add the mapping before adding an icon here.
 */
export const STORY_SECTION_ICONS = [
  'travel_explore',
  'explore',
  'public',
  'location_city',
  'map',
  'directions',
  'tour',
  'landscape',
  'beach_access',
  'ac_unit',
  'flight',
  'hotel',
  'villa',
  'bed',
  'room_service',
  'deck',
  'restaurant',
  'local_bar',
  'wine_bar',
  'coffee',
  'celebration',
  'calendar_month',
  'child_care',
  'family_restroom',
  'groups',
  'temple_buddhist',
  'photo_camera',
  'lock',
  'balance',
  'link',
] as const;
export const StorySectionIconSchema = z.enum(STORY_SECTION_ICONS);
export type StorySectionIcon = z.infer<typeof StorySectionIconSchema>;

/** The icon a client shows for a section whose stored icon it doesn't know. */
export const FALLBACK_STORY_SECTION_ICON: StorySectionIcon = 'travel_explore';

export function isStorySectionIcon(value: string): value is StorySectionIcon {
  return (STORY_SECTION_ICONS as readonly string[]).includes(value);
}

/**
 * The widths every story image is stored at. Each slot loads the smallest one that fills it sharply: 640
 * covers the rail cards (240 px on web at 2x, 180 pt on mobile at 3x), 1280 a phone-width article image
 * at 3x, 2048 the web article at up to 1024 px at 2x. A smaller original only gets the widths it can fill.
 */
export const STORY_IMAGE_WIDTHS = [640, 1280, 2048] as const;

export const StoryImageVariantSchema = z.object({
  width: z.number().int().positive().describe('Pixel width of this file'),
  url: z.string().url().describe('Public https URL of the WebP file'),
});
export type StoryImageVariant = z.infer<typeof StoryImageVariantSchema>;

/** One image, stored at several widths. `width`/`height` are the largest variant's, so they give its ratio. */
export const StoryImageAssetSchema = z.object({
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  variants: z.array(StoryImageVariantSchema).min(1),
});
export type StoryImageAsset = z.infer<typeof StoryImageAssetSchema>;

export const StoryEntityRefSchema = z.object({
  type: StoryItemTypeSchema,
  /** Firestore document id in the collection named by `type`. */
  id: z.string(),
});
export type StoryEntityRef = z.infer<typeof StoryEntityRefSchema>;

export const StoryImageSchema = z.object({
  asset: StoryImageAssetSchema,
  caption: z.string().nullish(),
});
export type StoryImage = z.infer<typeof StoryImageSchema>;

/**
 * One block of a story's article, rendered in order. `data` blocks with a single item render as a
 * full-width card, multiple as a carousel; `image` blocks with a single image render inline, multiple as a
 * carousel; `section` blocks render a header and form the article's internal nav.
 */
export const StoryBlockSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('text'), markdown: z.string() }),
  z.object({
    kind: z.literal('data'),
    items: z.array(StoryEntityRefSchema),
    /**
     * Also plot the block's items on a map. Web puts it full-width above the cards; mobile makes it the
     * carousel's first cell. Markers open the same guide the matching card does.
     */
    showMap: z.boolean().nullish(),
  }),
  /**
   * A section header and nav entry. `anchorOnly` keeps the nav entry and anchor but skips the visible
   * heading (e.g. a leading "Introduction" that shouldn't sit as a title above the intro prose).
   */
  z.object({
    kind: z.literal('section'),
    title: z.string(),
    icon: z.string(),
    anchorOnly: z.boolean().nullish(),
  }),
  z.object({ kind: z.literal('image'), images: z.array(StoryImageSchema) }),
  /** A map of the entities listed, one marker each; it fits itself to its markers. */
  z.object({ kind: z.literal('map'), items: z.array(StoryEntityRefSchema) }),
]);
export type StoryBlock = z.infer<typeof StoryBlockSchema>;
export type StoryBlockKind = StoryBlock['kind'];

/**
 * Reads stored blocks one at a time, skipping any this build can't parse — a block kind added after it
 * shipped, most likely — so the rest of the article still renders.
 */
export function parseStoryBlocks(blocks: unknown[]): StoryBlock[] {
  return blocks.flatMap((block) => {
    const parsed = StoryBlockSchema.safeParse(block);
    return parsed.success ? [parsed.data] : [];
  });
}

/** Content blocks as stored: read leniently by {@link parseStoryBlocks}. */
export const StoredStoryBlocksSchema = z.array(z.unknown()).transform(parseStoryBlocks);

export const STORY_STATUSES = ['draft', 'published', 'archived'] as const;
export const StoryStatusSchema = z.enum(STORY_STATUSES);
export type StoryStatus = z.infer<typeof StoryStatusSchema>;

/** The current shape of a story document. Bump it when the shape changes in a way old builds must notice. */
export const STORY_SCHEMA_VERSION = 1;

/**
 * The story fields an editor writes. Every field may be incomplete while the story is a draft; the publish
 * checks enforce the rest.
 */
export const StoryEditableSchema = z.object({
  title: z.string(),
  description: z.string(),
  author: StoryAuthorIdSchema,
  coverImage: StoryImageAssetSchema.nullish(),
  /**
   * Attribution for the cover, as markdown, rendered small at the foot of the article. Required whenever the
   * cover is someone else's photograph: a Creative Commons cover is only licensed while the credit ships
   * with it (TASL: title, author, source link, license link).
   */
  coverCredit: z.string().nullish(),
  content: z.array(StoryBlockSchema),
});
export type StoryEditable = z.infer<typeof StoryEditableSchema>;

/**
 * A story as a client renders it: published, with everything a card and an article need. A stored story
 * that fails this is left off the rail rather than shown half-built.
 */
export const PublishedStorySchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  description: z.string(),
  author: StoryAuthorIdSchema,
  coverImage: StoryImageAssetSchema,
  coverCredit: z.string().nullish(),
  content: z.array(StoryBlockSchema),
  publishAt: z.date(),
  expiresAt: z.date().nullish(),
});
export type Story = z.infer<typeof PublishedStorySchema>;

/**
 * Whether a published story is on the rail at `now`: its publish time has come and its expiry (if any)
 * hasn't. Status is checked by the query; this covers the two dates.
 */
export function isStoryLive(story: { publishAt?: Date | null; expiresAt?: Date | null }, now: Date): boolean {
  if (!story.publishAt || story.publishAt.getTime() > now.getTime()) return false;
  return !story.expiresAt || story.expiresAt.getTime() > now.getTime();
}

/** Where a story stands, as the admin shows it. Scheduled, live and expired are all `published`. */
export type StoryStage = 'draft' | 'scheduled' | 'live' | 'expired' | 'archived';

export function storyStage(
  story: { status: StoryStatus; publishAt?: Date | null; expiresAt?: Date | null },
  now: Date,
): StoryStage {
  if (story.status !== 'published') return story.status;
  if (!story.publishAt || story.publishAt.getTime() > now.getTime()) return 'scheduled';
  if (story.expiresAt && story.expiresAt.getTime() <= now.getTime()) return 'expired';
  return 'live';
}

/** The id from a `#preview=type&id=…` link in a text block's markdown, with its type. */
const PREVIEW_LINK = /#preview=(hotel|destination|place)&id=([A-Za-z0-9_-]+)/g;

/**
 * Every entity a story references: its data and map blocks' items, and the inline `#preview=` links in
 * its text. Used to check them all exist before a story goes live.
 */
export function storyEntityRefs(content: StoryBlock[]): StoryEntityRef[] {
  const refs: StoryEntityRef[] = [];
  for (const block of content) {
    if (block.kind === 'data' || block.kind === 'map') refs.push(...block.items);
    if (block.kind === 'text') {
      for (const match of block.markdown.matchAll(PREVIEW_LINK)) {
        refs.push({ type: match[1] as StoryItemType, id: match[2] as string });
      }
    }
  }
  const seen = new Set<string>();
  return refs.filter((ref) => {
    const key = `${ref.type}/${ref.id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Every image asset a story shows: its cover and each image block's images. */
export function storyImageAssets(story: { coverImage?: StoryImageAsset | null; content: StoryBlock[] }): StoryImageAsset[] {
  const assets = story.coverImage ? [story.coverImage] : [];
  for (const block of story.content) {
    if (block.kind === 'image') assets.push(...block.images.map((image) => image.asset));
  }
  return assets;
}

/**
 * The smallest variant at least `minWidth` pixels wide, or the largest there is. Mobile prefetch and
 * any caller that needs one URL rather than a `srcset` use this.
 */
export function pickStoryImageVariant(asset: StoryImageAsset, minWidth: number): StoryImageVariant {
  const byWidth = asset.variants.toSorted((a, b) => a.width - b.width);
  return byWidth.find((variant) => variant.width >= minWidth) ?? (byWidth.at(-1) as StoryImageVariant);
}

/**
 * The completeness a story needs before it goes live, as a list of problems (empty when it is ready). The
 * server adds the checks that need the database: every referenced entity exists. The description is
 * optional: both apps render a story without one (one of the launch stories has none).
 */
export function storyPublishProblems(story: StoryEditable): string[] {
  const problems: string[] = [];
  if (!story.title.trim()) problems.push('Add a title.');
  if (!story.coverImage) problems.push('Add a cover image.');
  if (story.content.length === 0) problems.push('Add at least one block.');
  for (const [index, block] of story.content.entries()) {
    const at = `Block ${index + 1}`;
    switch (block.kind) {
      case 'text':
        if (!block.markdown.trim()) problems.push(`${at}: the text is empty.`);
        break;
      case 'data':
      case 'map':
        if (block.items.length === 0) problems.push(`${at}: pick at least one hotel, destination or place.`);
        if (block.items.some((item) => !item.id.trim())) problems.push(`${at}: an entity has no id.`);
        break;
      case 'section':
        if (!block.title.trim()) problems.push(`${at}: the section has no title.`);
        if (!isStorySectionIcon(block.icon)) problems.push(`${at}: "${block.icon}" is not a section icon.`);
        break;
      case 'image':
        if (block.images.length === 0) problems.push(`${at}: add at least one image.`);
        break;
    }
  }
  return problems;
}
